import { useEffect, useRef } from 'react';
import { useField } from 'formik';
import { generateClassName } from '@klv/shared/utils/functions';

import Icon from '@klv/atoms/icon';
import {
  FieldGroup, Label, Error, Select,
} from '..';

import * as s from './styles/select.module.scss';

import type { TColorVariant } from '@klv/shared/styles/types';
import type { IIcon } from '@klv/atoms/icon/types';
import type { ISelectInput } from '../types';

export const SelectFieldGroup = ({
  backgroundColor = 'light',
  children,
  className,
  pricing
}: { 
  backgroundColor?: TColorVariant;
  children: React.ReactNode;
  className?: string;
  pricing?: boolean;
}) => (
  <FieldGroup className={generateClassName([s.selectFieldGroup, s[backgroundColor], pricing && s.pricing, className])}>
    {children}
  </FieldGroup>
);

const SelectArrow = ({
  backgroundColor = 'light',
  className,
  ...props
}: {
  backgroundColor?: TColorVariant;
  className?: string;
} & IIcon) => (
  <Icon
    className={generateClassName([s.selectArrow, s[backgroundColor], 'selectArrow'])}
    {...props} 
  />
);

// using the onChange prop was causing the SelectBox to not update values.
const useSelectInputOnValueChange = ({ handleChange, field }: any) => {
  // needed ref as change event was getting called multiple times.
  const fieldValue = useRef(null);

  useEffect(() => {
    if (!handleChange) return;
    if ((field?.value || field?.value === '') && field.value !== fieldValue.current) {
      fieldValue.current = field.value;
      handleChange(field.value);
    }
  }, [field, handleChange]);
};

const SelectInput = ({
  label,
  backgroundColor = 'dark',
  handleChange,
  ...props
}: ISelectInput) => {
  const [field, meta] = useField(props);
  useSelectInputOnValueChange({ field, handleChange });

  return (
    <SelectFieldGroup backgroundColor={backgroundColor}>
      <SelectArrow backgroundColor={backgroundColor} iconName="ChevronDown" fillColor="currentColor" />
      <Label $backgroundColor={backgroundColor} htmlFor={props.id || props.name}>{label}</Label>
      <Select $backgroundColor={backgroundColor} hasError={Boolean(meta.touched && meta.error)} {...field} {...props} />
      {(meta.touched && meta.error) && <Error>{meta.error}</Error>}
    </SelectFieldGroup>
  );
};

export default SelectInput;
